// Turns publication metadata into formatted reference strings using CSL
// (Citation Style Language), per the V1.4.5.1 doc. We map each paper to CSL-JSON
// and run it through citeproc-js with the user-selected .csl style + en-US locale.
// citeproc-js, the style files, and the locale are loaded on demand (this module's
// only consumer is the "Add from Existing Vertical Resource(s)" page).

// Selector key -> .csl filename in public/csl/. Keep STYLE_KEYS in display order.
export const STYLE_KEYS = ['MLA', 'APA', 'Chicago', 'IEEE', 'CSE', 'AMA'];

const STYLE_FILES = {
  MLA: 'mla.csl',
  APA: 'apa.csl',
  Chicago: 'chicago.csl',
  IEEE: 'ieee.csl',
  CSE: 'cse.csl',
  AMA: 'ama.csl',
};

// TSP publicationType -> CSL type (doc's mapping table). Defaults to article-journal.
const TYPE_MAP = {
  'Journal Article': 'article-journal',
  Book: 'book',
  'Book Chapter': 'chapter',
  'Conference Paper': 'paper-conference',
  Thesis: 'thesis',
  Dissertation: 'thesis',
  Report: 'report',
  Website: 'webpage',
  Webpage: 'webpage',
  Dataset: 'dataset',
  Patent: 'patent',
};

// TSP stores each author as a single `name` string; CSL wants {family, given}.
// Heuristic: last whitespace token = family, the rest = given.
const parseName = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { family: parts[0] };
  return { family: parts[parts.length - 1], given: parts.slice(0, -1).join(' ') };
};

// "2017-10-13" -> { 'date-parts': [[2017, 10, 13]] }; partial dates are fine.
const parseDateParts = (dateStr) => {
  if (!dateStr) return undefined;
  const nums = String(dateStr)
    .split('-')
    .map((p) => parseInt(p, 10))
    .filter((n) => !Number.isNaN(n));
  return nums.length ? { 'date-parts': [nums] } : undefined;
};

export const paperToCslJson = (paper) => {
  const doi = paper.externalIds?.DOI || '';
  const url = paper.openAccessPdf?.url || paper.url || (doi ? `https://doi.org/${doi}` : '');
  const issued = parseDateParts(paper.publicationDate);
  const authors = (paper.authors || []).map((a) => parseName(a.name)).filter(Boolean);

  const csl = {
    id: String(paper._id || paper.paperId),
    type: TYPE_MAP[paper.publicationType] || 'article-journal',
    title: paper.title || '',
  };
  if (authors.length) csl.author = authors;
  if (paper.venue) csl['container-title'] = paper.venue;
  if (issued) csl.issued = issued;
  if (doi) csl.DOI = doi;
  if (url) csl.URL = url;
  return csl;
};

// Numeric styles (IEEE/AMA/CSE) prefix each bibliography entry with "[1]"/"1.".
// We store references as standalone items, so drop the leading enumerator.
const stripEnumerator = (text) =>
  text.replace(/^\s*\[\d+\]\s*/, '').replace(/^\s*\d+\.\s+/, '');

const cache = { locale: null, styles: {} };

const loadText = async (file) => {
  const res = await fetch(`${process.env.PUBLIC_URL}/csl/${file}`);
  if (!res.ok) throw new Error(`Failed to load CSL asset: ${file}`);
  return res.text();
};

const getLocale = async () => {
  if (!cache.locale) cache.locale = await loadText('locales-en-US.xml');
  return cache.locale;
};

const getStyle = async (file) => {
  if (!cache.styles[file]) cache.styles[file] = await loadText(file);
  return cache.styles[file];
};

// Format each paper individually (preserves selection order and avoids the
// alphabetical re-sorting author-date styles apply to a multi-item bibliography).
// Returns [{ reference, url }] aligned to the input order.
export const formatReferences = async (papers, styleKey) => {
  const file = STYLE_FILES[styleKey] || STYLE_FILES.APA;
  const [mod, styleXml, localeXml] = await Promise.all([
    import('citeproc'),
    getStyle(file),
    getLocale(),
  ]);
  const CSL = mod.default || mod;

  const items = papers.map(paperToCslJson);
  const itemMap = {};
  items.forEach((it) => {
    itemMap[it.id] = it;
  });

  const sys = {
    retrieveLocale: () => localeXml,
    retrieveItem: (id) => itemMap[id],
  };
  const engine = new CSL.Engine(sys, styleXml);
  engine.setOutputFormat('text');

  return items.map((it, i) => {
    engine.updateItems([it.id]);
    const bib = engine.makeBibliography();
    const raw = bib && bib[1] && bib[1][0] ? bib[1][0] : '';
    const reference = stripEnumerator(raw).trim() || papers[i].title || 'Untitled';
    return { reference, url: it.DOI || it.URL || '' };
  });
};
